import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit, Trash2, Eye, User, Users, Mail, Phone, 
  MapPin, Building, Building2, CreditCard, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown,
  X, AlertCircle, CheckCircle, XCircle, Filter, Download, Upload, 
  Calendar, FileText, DollarSign, TrendingUp, TrendingDown, Clock, 
  Settings, Package, ShoppingBag, Smartphone, Copy, Star, Sparkles,
  Percent, Tag, Boxes, AlertTriangle, Coffee, BarChart3,
  ShoppingCart, LogOut, Menu, FileSpreadsheet, Image as ImageIcon
} from 'lucide-react';
import { empresasService } from '../../../services/api';

const FeedbackModal = ({ isOpen, type, title, message, onConfirm, onClose, showCancel }) => {
  if (!isOpen) return null;

  const icons = {
    success: <CheckCircle className="text-green-500 w-12 h-12" />,
    error: <XCircle className="text-red-500 w-12 h-12" />,
    warning: <AlertTriangle className="text-yellow-500 w-12 h-12" />,
    info: <AlertCircle className="text-blue-500 w-12 h-12" />
  };

  const colors = {
    success: 'bg-green-600 hover:bg-green-700',
    error: 'bg-red-600 hover:bg-red-700',
    warning: 'bg-yellow-600 hover:bg-yellow-700',
    info: 'bg-blue-600 hover:bg-blue-700'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center transform transition-all">
        <div className="flex justify-center mb-4">
          {icons[type] || icons.info}
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex justify-center gap-3">
          {showCancel && (
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
          )}
          <button
            onClick={() => {
              if (onConfirm) onConfirm();
              onClose();
            }}
            className={`px-4 py-2 text-white rounded-lg transition-colors ${colors[type] || colors.info}`}
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
};

const EmpresaModal = ({ mode, empresa, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    tipo_persona: 'juridica',
    cedula: '',
    rif: '',
    telefono: '',
    email: '',
    direccion: '',
    activo: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (empresa) {
      setFormData({
        nombre: empresa.nombre || '',
        tipo_persona: empresa.tipo_persona || 'juridica',
        cedula: empresa.cedula || '',
        rif: empresa.rif || '',
        telefono: empresa.telefono || '',
        email: empresa.email || '',
        direccion: empresa.direccion || '',
        activo: empresa.activo !== undefined ? empresa.activo : true
      });
    }
  }, [empresa]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let mensajeExito = '';
      if (mode === 'create') {
        await empresasService.create(formData);
        mensajeExito = 'Empresa creada exitosamente';
      } else if (mode === 'edit') {
        await empresasService.update(empresa.id, formData);
        mensajeExito = 'Empresa actualizada exitosamente';
      }
      onSuccess(mensajeExito);
    } catch (err) {
      setError(err.message || 'Error al guardar empresa');
    } finally {
      setLoading(false);
    }
  };

  const isViewMode = mode === 'view';

  // Verificar si faltan datos
  const datosIncompletos = formData.tipo_persona === 'natural' 
    ? (!formData.cedula || formData.cedula.trim() === '')
    : (!formData.rif || formData.rif.trim() === '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">
              {mode === 'create' && '🏢 Nueva Empresa'}
              {mode === 'edit' && '✏️ Editar Empresa'}
              {mode === 'view' && '👁️ Detalles de la Empresa'}
            </h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Advertencia de datos incompletos */}
          {!isViewMode && datosIncompletos && (
            <div className="bg-orange-100 border border-orange-400 text-orange-700 px-4 py-3 rounded mb-4 flex items-start gap-2">
              <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">⚠️ Datos incompletos</p>
                <p className="text-sm">
                  {formData.tipo_persona === 'natural' 
                    ? 'Se recomienda agregar la cédula de identidad.'
                    : 'Se recomienda agregar el RIF de la empresa.'}
                </p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Tipo de Persona */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Tipo <span className="text-red-500">*</span>
              </label>
              <select
                name="tipo_persona"
                value={formData.tipo_persona}
                onChange={handleChange}
                disabled={isViewMode}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="natural">Persona Natural</option>
                <option value="juridica">Persona Jurídica</option>
              </select>
            </div>

            {/* Nombre */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Nombre {formData.tipo_persona === 'natural' ? 'completo' : 'de la empresa'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                disabled={isViewMode}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder={formData.tipo_persona === 'natural' ? 'Ej: Juan Pérez' : 'Ej: Gaman C.A.'}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Cédula o RIF según tipo */}
              {formData.tipo_persona === 'natural' ? (
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Cédula {datosIncompletos && <span className="text-orange-500">(Recomendado)</span>}
                  </label>
                  <input
                    type="text"
                    name="cedula"
                    value={formData.cedula}
                    onChange={handleChange}
                    disabled={isViewMode}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 disabled:bg-gray-100 ${
                      datosIncompletos 
                        ? 'border-orange-300 focus:ring-orange-500' 
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="Ej: V-12345678"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    RIF {datosIncompletos && <span className="text-orange-500">(Recomendado)</span>}
                  </label>
                  <input
                    type="text"
                    name="rif"
                    value={formData.rif}
                    onChange={handleChange}
                    disabled={isViewMode}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 disabled:bg-gray-100 ${
                      datosIncompletos 
                        ? 'border-orange-300 focus:ring-orange-500' 
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="Ej: J-123456789"
                  />
                </div>
              )}

              {/* Teléfono */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">Teléfono</label>
                <input
                  type="text"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  disabled={isViewMode}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  placeholder="Ej: 414-1234567"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={isViewMode}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Ej: empresa@email.com"
              />
            </div>

            {/* Dirección */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Dirección</label>
              <textarea
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                disabled={isViewMode}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Dirección completa"
              />
            </div>

            {/* Estado Activo */}
            {mode === 'edit' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="activo"
                  checked={formData.activo}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="ml-2 text-gray-700 font-medium">Empresa Activa</label>
              </div>
            )}

            {/* Info adicional en modo vista */}
            {isViewMode && empresa && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Calendar size={18} />
                  Información del Sistema
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">ID:</p>
                    <p className="font-semibold">{empresa.id}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Creado por:</p>
                    <p className="font-semibold">{empresa.creador_nombre || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Fecha de creación:</p>
                    <p className="font-semibold">
                      {new Date(empresa.created_at).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Última actualización:</p>
                    <p className="font-semibold">
                      {new Date(empresa.updated_at).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                </div>

                {/* Mostrar advertencia si faltan datos */}
                {empresa.datos_incompletos === 1 && (
                  <div className="mt-4 bg-orange-50 border border-orange-200 rounded p-3 flex items-start gap-2">
                    <AlertTriangle size={18} className="text-orange-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-orange-800">⚠️ Faltan datos importantes</p>
                      <p className="text-orange-700">
                        {empresa.tipo_persona === 'natural' 
                          ? 'Esta persona no tiene cédula registrada.'
                          : 'Esta empresa no tiene RIF registrado.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {isViewMode ? 'Cerrar' : 'Cancelar'}
          </button>
          {!isViewMode && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`px-6 py-2 rounded-lg text-white ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EmpresasView = () => {
  const [empresas, setEmpresas] = useState([]);
  const [empresasFiltradas, setEmpresasFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
  const [stats, setStats] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('nombre');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedCards, setExpandedCards] = useState({});
  const [feedback, setFeedback] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null, showCancel: false });

  const showFeedback = (type, title, message, onConfirm = null, showCancel = false) => {
    setFeedback({ isOpen: true, type, title, message, onConfirm, showCancel });
  };

  const closeFeedback = () => setFeedback(prev => ({ ...prev, isOpen: false }));

  const toggleCardExpansion = (empresaId) => {
    setExpandedCards(prev => ({
      ...prev,
      [empresaId]: !prev[empresaId]
    }));
  };

  const cargarEmpresas = async () => {
    try {
      setLoading(true);
      const response = await empresasService.getAll({});
      setEmpresas(response.data);
      setEmpresasFiltradas(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
      showFeedback('error', 'Error', error.message || 'Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const cargarStats = async () => {
    try {
      const response = await empresasService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  useEffect(() => {
    cargarEmpresas();
    cargarStats();
  }, []);

  useEffect(() => {
    let resultado = [...empresas];

    if (tipoFiltro) {
      resultado = resultado.filter(e => e.tipo_persona === tipoFiltro);
    }

    if (searchTerm) {
      resultado = resultado.filter(e =>
        e.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.cedula && e.cedula.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (e.rif && e.rif.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (e.email && e.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    resultado.sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setEmpresasFiltradas(resultado);
    setCurrentPage(1);
  }, [searchTerm, tipoFiltro, empresas, sortColumn, sortDirection]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = empresasFiltradas.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(empresasFiltradas.length / itemsPerPage);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleNuevaEmpresa = () => {
    setModalMode('create');
    setEmpresaSeleccionada(null);
    setShowModal(true);
  };

  const handleEditarEmpresa = (empresa) => {
    setModalMode('edit');
    setEmpresaSeleccionada(empresa);
    setShowModal(true);
  };

  const handleEliminarEmpresa = (id) => {
    showFeedback(
      'warning',
      'Confirmar Eliminación',
      '¿Estás seguro de eliminar esta empresa?',
      async () => {
        try {
          await empresasService.delete(id);
          showFeedback('success', 'Éxito', 'Empresa eliminada exitosamente');
          cargarEmpresas();
          cargarStats();
        } catch (error) {
          showFeedback('error', 'Error', error.message || 'Error al eliminar empresa');
        }
      },
      true
    );
  };

  const handleVerEmpresa = (empresa) => {
    setModalMode('view');
    setEmpresaSeleccionada(empresa);
    setShowModal(true);
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800">
            Gestión de Empresas/Zonas
          </h2>
          <p className="text-sm md:text-base text-gray-600">
            Administra las empresas y personas asociadas
          </p>
        </div>
        <button 
          onClick={handleNuevaEmpresa}
          className="bg-blue-600 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors w-full sm:w-auto text-sm md:text-base"
        >
          <Plus size={20} /> 
          <span>Nueva Empresa</span>
        </button>
      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs md:text-sm mb-1">Total</p>
                <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800">
                  {stats.total}
                </p>
              </div>
              <Building2 className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs md:text-sm mb-1">P. Naturales</p>
                <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800">
                  {stats.naturales}
                </p>
              </div>
              <Users className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs md:text-sm mb-1">P. Jurídicas</p>
                <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800">
                  {stats.juridicas}
                </p>
              </div>
              <Building className="text-purple-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs md:text-sm mb-1">Activas</p>
                <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800">
                  {stats.activos}
                </p>
              </div>
              <CheckCircle className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-3 md:p-4 col-span-2 md:col-span-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs md:text-sm mb-1">Incompletos</p>
                <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800">
                  {stats.incompletos}
                </p>
              </div>
              <AlertTriangle className="text-orange-500" size={24} />
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow mb-4 p-3 md:p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <input 
            type="text" 
            placeholder="Buscar..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 md:px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select 
            value={tipoFiltro}
            onChange={(e) => setTipoFiltro(e.target.value)}
            className="px-3 md:px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos los tipos</option>
            <option value="natural">Persona Natural</option>
            <option value="juridica">Persona Jurídica</option>
          </select>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-3 md:px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </select>
        </div>
      </div>

      {/* Vista Unificada - Tarjetas estilo Solicitudes */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando empresas...</p>
        </div>
      ) : empresasFiltradas.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Building2 size={64} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 text-lg">No se encontraron empresas</p>
        </div>
      ) : (
        <div className="space-y-4">
          {currentItems.map(empresa => {
            const isExpanded = expandedCards[empresa.id];
            return (
              <div key={empresa.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200">

                {/* ── Header de la tarjeta ── */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 rounded-t-lg border-b border-gray-200">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3 flex-1">
                      {empresa.datos_incompletos === 1 && (
                        <AlertTriangle size={20} className="text-orange-500 flex-shrink-0" title="Faltan datos: Cédula/RIF" />
                      )}
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-blue-600">{empresa.nombre}</h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                            empresa.tipo_persona === 'natural'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {empresa.tipo_persona === 'natural' ? 'Natural' : 'Jurídica'}
                          </span>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                            empresa.activo
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {empresa.activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 items-start">
                      <button
                        onClick={() => toggleCardExpansion(empresa.id)}
                        className="text-gray-600 hover:text-gray-800 hover:bg-gray-200 p-2 rounded-lg transition-colors"
                        title={isExpanded ? 'Colapsar' : 'Expandir'}
                      >
                        {isExpanded
                          ? <ChevronUp size={18} />
                          : <ChevronDown size={18} />}
                      </button>
                      <button
                        onClick={() => handleVerEmpresa(empresa)}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-lg transition-colors"
                        title="Ver detalles"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleEditarEmpresa(empresa)}
                        className="text-green-600 hover:text-green-800 hover:bg-green-100 p-2 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleEliminarEmpresa(empresa.id)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-100 p-2 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── Vista colapsada (resumen) ── */}
                {!isExpanded && (
                  <div className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {/* Tipo */}
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Tipo</p>
                        <p className="text-sm text-gray-800 truncate">
                          {empresa.tipo_persona === 'natural' ? 'Persona Natural' : 'Persona Jurídica'}
                        </p>
                      </div>
                      {/* Cédula / RIF */}
                      <div>
                        <p className="text-xs text-gray-500 font-medium">
                          {empresa.tipo_persona === 'natural' ? 'Cédula' : 'RIF'}
                        </p>
                        <p className="text-sm truncate">
                          {empresa.tipo_persona === 'natural'
                            ? (empresa.cedula || <span className="text-orange-500">Sin cédula</span>)
                            : (empresa.rif     || <span className="text-orange-500">Sin RIF</span>)}
                        </p>
                      </div>
                      {/* Teléfono */}
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Teléfono</p>
                        <p className="text-sm text-gray-800 truncate">{empresa.telefono || '-'}</p>
                      </div>
                      {/* Email */}
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Email</p>
                        <p className="text-sm text-gray-800 truncate">{empresa.email || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Vista expandida (detalle) ── */}
                {isExpanded && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

                      {/* Tipo de persona */}
                      <div className="flex items-start gap-2">
                        <Users size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Tipo</p>
                          <p className="text-sm text-gray-800">
                            {empresa.tipo_persona === 'natural' ? 'Persona Natural' : 'Persona Jurídica'}
                          </p>
                        </div>
                      </div>

                      {/* Cédula / RIF */}
                      <div className="flex items-start gap-2">
                        <CreditCard size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">
                            {empresa.tipo_persona === 'natural' ? 'Cédula' : 'RIF'}
                          </p>
                          <p className="text-sm">
                            {empresa.tipo_persona === 'natural'
                              ? (empresa.cedula || <span className="text-orange-500 font-semibold">Sin cédula</span>)
                              : (empresa.rif     || <span className="text-orange-500 font-semibold">Sin RIF</span>)}
                          </p>
                        </div>
                      </div>

                      {/* Teléfono */}
                      <div className="flex items-start gap-2">
                        <Phone size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Teléfono</p>
                          <p className="text-sm text-gray-800">{empresa.telefono || '-'}</p>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex items-start gap-2">
                        <Mail size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Email</p>
                          <p className="text-sm text-gray-800 break-all">{empresa.email || '-'}</p>
                        </div>
                      </div>

                      {/* Estado */}
                      <div className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Estado</p>
                          <span className={`inline-block px-2 py-1 rounded text-xs font-semibold mt-0.5 ${
                            empresa.activo
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {empresa.activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </div>
                      </div>

                      {/* Dirección */}
                      {empresa.direccion && (
                        <div className="flex items-start gap-2 md:col-span-2">
                          <MapPin size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500 font-medium">Dirección</p>
                            <p className="text-sm text-gray-800">{empresa.direccion}</p>
                          </div>
                        </div>
                      )}

                      {/* Datos incompletos */}
                      {empresa.datos_incompletos === 1 && (
                        <div className="flex items-start gap-2">
                          <AlertTriangle size={16} className="text-orange-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500 font-medium">Aviso</p>
                            <span className="inline-block px-2 py-1 rounded text-xs font-semibold bg-orange-100 text-orange-800 mt-0.5">
                              Datos incompletos
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Paginación */}
      <div className="bg-white rounded-lg shadow mt-4 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-gray-700 text-center sm:text-left">
            Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, empresasFiltradas.length)} de {empresasFiltradas.length} registros
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              Primero
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              Anterior
            </button>
            <span className="px-3 py-1 text-sm">
              Página {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              Siguiente
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              Último
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <EmpresaModal
          mode={modalMode}
          empresa={empresaSeleccionada}
          onClose={() => {
            setShowModal(false);
            setEmpresaSeleccionada(null);
          }}
          onSuccess={(mensaje) => {
            setShowModal(false);
            setEmpresaSeleccionada(null);
            cargarEmpresas();
            cargarStats();
            if (mensaje) {
              showFeedback('success', 'Éxito', mensaje);
            }
          }}
        />
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        {...feedback}
        onClose={closeFeedback}
      />
    </div>
  );
};

export default EmpresasView;